// src/pages/ventes/FacturesImpayees.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Badge,
    Modal, Spinner, Alert, Tooltip, OverlayTrigger, Pagination, InputGroup
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { MODE_REGLEMENT } from "../utils/constantes";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

export default function FacturesImpayees() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [factureSelectionnee, setFactureSelectionnee] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date_echeance', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [paiement, setPaiement] = useState({
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        date_reglement: new Date().toISOString().split('T')[0]
    });
    const [saving, setSaving] = useState(false);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });
    const showSuccess = (msg) => showToast('success', 'Succès', msg);
    const showError   = (msg) => showToast('error', 'Erreur', msg);

    useEffect(() => {
        fetchImpayees();
        fetchStats();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchImpayees = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=impayees`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setFactures(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    /* ============================================================
       PAIEMENT
       ============================================================ */
    const handlePaiementSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            if (!user || !user.id) {
                showError("Utilisateur non identifié");
                setSaving(false);
                return;
            }

            const response = await fetch(`${API_BASE_URL}/factures.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'encaisser',
                    id_facture: factureSelectionnee.id_facture,
                    montant: parseFloat(paiement.montant),
                    mode_reglement: paiement.mode_reglement,
                    reference: paiement.reference,
                    id_utilisateur: user.id
                })
            });

            const result = await response.json();

            if (result.success) {
                setShowPaiementModal(false);
                fetchImpayees();
                fetchStats();
                showSuccess("Paiement enregistré avec succès");
                setPaiement({
                    montant: '',
                    mode_reglement: 'especes',
                    reference: '',
                    date_reglement: new Date().toISOString().split('T')[0]
                });
            } else {
                showError(result.message || "Erreur lors du paiement");
            }
        } catch (err) {
            console.error("Erreur paiement:", err);
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getJoursRetard = (dateEcheance) => {
        const today = new Date();
        const echeance = new Date(dateEcheance);
        const diffTime = today - echeance;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getResteCouleur = (reste, joursRetard) => {
        if (reste === 0) return 'text-success';
        if (joursRetard > 7) return 'text-danger fw-bold';
        if (joursRetard > 0) return 'text-danger';
        return 'text-warning';
    };

    const getRetardBadge = (joursRetard) => {
        const estEnRetard = joursRetard > 0;
        const gradient = estEnRetard
            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
            : 'linear-gradient(135deg, #10b981, #059669)';
        return (
            <span className="status-badge-charte" style={{ background: gradient }}>
                <i className={estEnRetard ? 'ri-alarm-warning-line' : 'ri-check-line'}></i>
                {estEnRetard ? `J+${joursRetard}` : `J-${Math.abs(joursRetard)}`}
            </span>
        );
    };

    /* ============================================================
       FILTER / SORT / PAGINATION
       ============================================================ */
    const filteredFactures = useMemo(() => {
        return factures
            .filter(f =>
                f.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                const aRetard = getJoursRetard(a.date_echeance) > 0;
                const bRetard = getJoursRetard(b.date_echeance) > 0;
                if (aRetard && !bRetard) return -1;
                if (!aRetard && bRetard) return 1;
                if (sortConfig.key === 'date_echeance') {
                    return sortConfig.direction === 'asc'
                        ? getJoursRetard(a.date_echeance) - getJoursRetard(b.date_echeance)
                        : getJoursRetard(b.date_echeance) - getJoursRetard(a.date_echeance);
                }
                if (sortConfig.key === 'montant') {
                    const resteA = a.montant_ttc - (a.montant_regle || 0);
                    const resteB = b.montant_ttc - (b.montant_regle || 0);
                    return sortConfig.direction === 'asc' ? resteA - resteB : resteB - resteA;
                }
                return 0;
            });
    }, [factures, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    const totalImpaye = currentItems.reduce((acc, f) => acc + (f.montant_ttc - (f.montant_regle || 0)), 0);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ icon, onClick, tooltip, color, as: Component = 'button', to }) => {
        const content = (
            <Component
                className="action-btn-charte"
                onClick={onClick}
                {...(to ? { to } : {})}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </Component>
        );
        if (Component === Link) return content;
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
                {content}
            </OverlayTrigger>
        );
    };

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-error-warning-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des factures impayées
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchImpayees} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                <i className="ri-error-warning-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Factures impayées</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-file-list-line"></i>
                                        {filteredFactures.length} facture(s)
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-money-euro-circle-line"></i>
                                        {formatCurrency(totalImpaye)} en jeu
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/ventes/factures')}>
                                <i className="ri-arrow-left-line"></i> Toutes les factures
                            </Button>
                            <Button className="btn-modern-outline" onClick={fetchImpayees} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total impayés</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(totalImpaye)}</h3>
                                    <span className="kpi-unit-charte">{filteredFactures.length} facture(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-timer-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Retard maximum</span>
                                    <h3 className="kpi-value-charte">
                                        {currentItems.length > 0
                                            ? Math.max(...currentItems.map(f => { const jours = getJoursRetard(f.date_echeance); return jours > 0 ? jours : 0; }))
                                            : 0} <span style={{ fontSize: '0.7em' }}>jours</span>
                                    </h3>
                                    <span className="kpi-unit-charte">À traiter urgemment</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Clients concernés</span>
                                    <h3 className="kpi-value-charte">{new Set(factures.map(f => f.id_client)).size}</h3>
                                    <span className="kpi-unit-charte">clients débiteurs</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-bar-chart-2-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Moy. par facture</span>
                                    <h3 className="kpi-value-charte">
                                        {filteredFactures.length > 0
                                            ? formatCurrency(totalImpaye / filteredFactures.length)
                                            : formatCurrency(0)}
                                    </h3>
                                    <span className="kpi-unit-charte">montant moyen</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher facture, client, code..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="count-badge-charte">
                                            <i className="ri-stack-line"></i>
                                            {filteredFactures.length} facture(s) impayée(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient" style={{ background: `linear-gradient(135deg, ${COLORS.danger} 0%, #b91c1c 100%)` }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-error-warning-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Factures impayées</span>
                                    <span className="badge-header-light">{filteredFactures.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">N° Facture</th>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <SortableHeader field="date_echeance">Échéance</SortableHeader>
                                            <th className="text-end">Montant</th>
                                            <th className="text-end">Réglé</th>
                                            <SortableHeader field="montant" className="text-end">Reste</SortableHeader>
                                            <th>Progression</th>
                                            <th className="text-center">Retard</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((facture) => {
                                            const reste = facture.montant_ttc - (facture.montant_regle || 0);
                                            const joursRetard = getJoursRetard(facture.date_echeance);
                                            const estEnRetard = joursRetard > 0;
                                            const progression = (facture.montant_regle / facture.montant_ttc) * 100;

                                            return (
                                                <tr key={facture.id_facture} className={estEnRetard ? 'row-late-charte' : ''}>
                                                    <td className="ps-4">
                                                        <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="table-link-charte">
                                                            {facture.numero_facture}
                                                        </Link>
                                                    </td>
                                                    <td className="text-muted">{formatDate(facture.date_facture)}</td>
                                                    <td style={{ minWidth: '140px' }}>
                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{facture.client_nom}</div>
                                                        <small className="text-muted">{facture.code_client}</small>
                                                    </td>
                                                    <td>
                                                        <span className={estEnRetard ? 'text-danger fw-semibold' : 'text-muted'}>
                                                            {formatDate(facture.date_echeance)}
                                                        </span>
                                                        {estEnRetard && (
                                                            <span className="badge-late-charte">
                                                                <i className="ri-time-line"></i>En retard
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>{formatCurrency(facture.montant_ttc)}</td>
                                                    <td className="text-end" style={{ color: COLORS.success }}>{formatCurrency(facture.montant_regle || 0)}</td>
                                                    <td className={`text-end fw-semibold ${getResteCouleur(reste, joursRetard)}`}>{formatCurrency(reste)}</td>
                                                    <td style={{ minWidth: '110px' }}>
                                                        <div className="d-flex flex-column">
                                                            <div className="progress-charte-small">
                                                                <div
                                                                    className="progress-bar-charte-small"
                                                                    style={{
                                                                        width: `${progression}%`,
                                                                        background: progression === 100
                                                                            ? `linear-gradient(90deg, ${COLORS.success}, #059669)`
                                                                            : `linear-gradient(90deg, ${COLORS.warning}, #d97706)`
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <small className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>{Math.round(progression)}% payé</small>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">{getRetardBadge(joursRetard)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="action-btn-charte" style={{ '--btn-color': COLORS.primary }}>
                                                                    <i className="ri-eye-line"></i>
                                                                </Link>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Encaisser</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    style={{ '--btn-color': COLORS.success }}
                                                                    onClick={() => {
                                                                        setFactureSelectionnee(facture);
                                                                        setPaiement({ ...paiement, montant: reste });
                                                                        setShowPaiementModal(true);
                                                                    }}
                                                                >
                                                                    <i className="ri-bank-card-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Créer un avoir</Tooltip>}>
                                                                <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="action-btn-charte" style={{ '--btn-color': COLORS.warning }}>
                                                                    <i className="ri-file-copy-line"></i>
                                                                </Link>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="10">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}15, ${COLORS.success}05)`, color: COLORS.success }}>
                                                            <i className="ri-checkbox-circle-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune facture impayée</h6>
                                                        <p className="text-muted small mb-0">Toutes les factures sont soldées</p>
                                                        {filterText && (
                                                            <Button
                                                                onClick={() => setFilterText('')}
                                                                className="btn-modern-outline mt-3"
                                                                size="sm"
                                                            >
                                                                <i className="ri-refresh-line me-1"></i>Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{indexOfFirstItem + 1}</strong> à <strong>{Math.min(indexOfLastItem, filteredFactures.length)}</strong> sur <strong>{filteredFactures.length}</strong> facture(s)
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL PAIEMENT ===== */}
            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-bank-card-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Enregistrer un paiement</Modal.Title>
                            <p className="text-white-50 mb-0 small">Saisissez les informations du règlement</p>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handlePaiementSubmit}>
                    <Modal.Body className="p-4">
                        {factureSelectionnee && (
                            <>
                                <div className="summary-card-charte mb-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.95rem' }}>
                                                {factureSelectionnee.numero_facture}
                                            </div>
                                            <div className="small text-muted">{factureSelectionnee.client_nom}</div>
                                        </div>
                                        <span className="badge-header-light" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                            Reste: {formatCurrency(factureSelectionnee.montant_ttc - (factureSelectionnee.montant_regle || 0))}
                                        </span>
                                    </div>
                                </div>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="form-label-modern">Date de paiement</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="date"
                                                    value={paiement.date_reglement}
                                                    onChange={(e) => setPaiement({ ...paiement, date_reglement: e.target.value })}
                                                    required
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="form-label-modern">Montant</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0.01"
                                                    max={factureSelectionnee.montant_ttc - (factureSelectionnee.montant_regle || 0)}
                                                    value={paiement.montant}
                                                    onChange={(e) => setPaiement({ ...paiement, montant: e.target.value })}
                                                    required
                                                />
                                                <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label className="form-label-modern">Mode de règlement</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-bank-line"></i></InputGroup.Text>
                                        <Form.Select
                                            value={paiement.mode_reglement}
                                            onChange={(e) => setPaiement({ ...paiement, mode_reglement: e.target.value })}
                                            required
                                        >
                                            {MODE_REGLEMENT.map(mode => (
                                                <option key={mode.value} value={mode.value}>{mode.label}</option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                </Form.Group>

                                <Form.Group className="mb-0">
                                    <Form.Label className="form-label-modern">Référence (optionnel)</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="N° chèque, référence virement..."
                                            value={paiement.reference}
                                            onChange={(e) => setPaiement({ ...paiement, reference: e.target.value })}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button onClick={() => setShowPaiementModal(false)} className="btn-modern-outline">Annuler</Button>
                        <Button type="submit" disabled={saving} className="btn-modern-success">
                            {saving ? (
                                <><Spinner size="sm" className="me-2" />Enregistrement...</>
                            ) : (
                                <><i className="ri-check-line me-2"></i>Enregistrer</>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--danger);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte { background: #fef2f2; }
                .table-charte tbody tr.row-late-charte:hover { background: #fee2e2; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                }
                .badge-late-charte {
                    display: inline-flex; align-items: center; gap: 3px;
                    background: var(--danger); color: white;
                    padding: 0.15rem 0.5rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                    margin-left: 0.5rem;
                    animation: pulse-soft 2s infinite;
                }
                @keyframes pulse-soft {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
                .progress-charte-small {
                    height: 6px; border-radius: 4px;
                    background: #e2e8f0; overflow: hidden;
                }
                .progress-bar-charte-small {
                    height: 100%; border-radius: 4px;
                    transition: width 0.4s ease;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    text-decoration: none;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                    text-decoration: none;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #ffffff, #f8fafc);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-success:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTE ERREUR ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--danger);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--danger);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}